import React, { useState } from "react";
import { supabase } from "./supabase";
import "./App.css";

export default function Profile({ onClose, user, profile, onProfileUpdate }) {
    const [tab, setTab] = useState("profile");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [form, setForm] = useState({
        full_name: profile?.full_name ?? "",
    });
    const [pwForm, setPwForm] = useState({
        current: "",
        newPw: "",
        confirm: "",
    });

    // ── Update profile ────────────────────────────────
    async function handleUpdateProfile() {
        if (!form.full_name) { setError("Name is required!"); return; }
        setSaving(true);
        setError(null);

        const { error } = await supabase
            .from("profiles")
            .update({ full_name: form.full_name })
            .eq("id", user.id);

        if (error) {
            setError(error.message);
        } else {
            setSuccess("✅ Profile updated!");
            onProfileUpdate?.({ ...profile, full_name: form.full_name });
            setTimeout(() => setSuccess(null), 3000);
        }
        setSaving(false);
    }

    // ── Change password ───────────────────────────────
    async function handleChangePassword() {
        if (!pwForm.newPw || !pwForm.confirm) {
            setError("Please fill in all password fields!");
            return;
        }
        if (pwForm.newPw !== pwForm.confirm) {
            setError("New passwords don't match!");
            return;
        }
        if (pwForm.newPw.length < 6) {
            setError("Password must be at least 6 characters!");
            return;
        }

        setSaving(true);
        setError(null);

        const { error } = await supabase.auth.updateUser({
            password: pwForm.newPw
        });

        if (error) { setError(error.message); }
        else {
            setSuccess("✅ Password changed successfully!");
            setPwForm({ current: "", newPw: "", confirm: "" });
            setTimeout(() => setSuccess(null), 3000);
        }
        setSaving(false);
    }

    // ── Forgot password ───────────────────────────────
    async function handleForgotPassword() {
        const { error } = await supabase.auth.resetPasswordForEmail(
            user.email,
            { redirectTo: `${window.location.origin}` }
        );
        if (error) alert("Error: " + error.message);
        else alert(`✅ Password reset email sent to ${user.email}`);
    }

    return (
        <div className="report-overlay">
            <div className="report-panel" style={{ maxWidth: "480px" }}>

                {/* Header */}
                <div className="report-header">
                    <div>
                        <h2 className="report-title">👤 My Profile</h2>
                        <p className="report-date">{user?.email}</p>
                    </div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <div className="inv-tabs">
                            <button
                                className={`inv-tab ${tab === "profile" ? "active" : ""}`}
                                onClick={() => { setTab("profile"); setError(null); setSuccess(null); }}
                            >
                                Profile
                            </button>
                            <button
                                className={`inv-tab ${tab === "password" ? "active" : ""}`}
                                onClick={() => { setTab("password"); setError(null); setSuccess(null); }}
                            >
                                Password
                            </button>
                        </div>
                    </div>
                    <button className="report-close" onClick={onClose}>✕</button>
                </div>

                <div className="wh-form-wrap">

                    {/* Messages */}
                    {error && (
                        <div style={{ padding: "10px 14px", background: "var(--red-dim)", border: "1px solid rgba(255,69,58,0.3)", borderRadius: "var(--radius)", color: "var(--red)", fontSize: "13px" }}>
                            ⚠ {error}
                        </div>
                    )}
                    {success && (
                        <div style={{ padding: "10px 14px", background: "var(--green-dim)", border: "1px solid rgba(50,215,75,0.3)", borderRadius: "var(--radius)", color: "var(--green)", fontSize: "13px" }}>
                            {success}
                        </div>
                    )}

                    {/* ── Profile Tab ── */}
                    {tab === "profile" && (
                        <div className="wh-section">
                            {/* Avatar */}
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
                                <div style={{
                                    width: "72px", height: "72px", borderRadius: "50%",
                                    background: profile?.role === "manager" ? "var(--cyan-dim)" : "var(--amber-dim)",
                                    border: `2px solid ${profile?.role === "manager" ? "var(--cyan)" : "var(--amber)"}`,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: "28px", fontWeight: 700,
                                    color: profile?.role === "manager" ? "var(--cyan)" : "var(--amber)",
                                }}>
                                    {profile?.full_name?.charAt(0)?.toUpperCase() ?? "?"}
                                </div>
                                <div style={{ textAlign: "center" }}>
                                    <p style={{ fontWeight: 700, color: "var(--text)", fontSize: "16px" }}>
                                        {profile?.full_name}
                                    </p>
                                    <span style={{
                                        fontSize: "11px", fontWeight: 700, padding: "2px 10px",
                                        borderRadius: "20px", textTransform: "uppercase", letterSpacing: "0.8px",
                                        background: profile?.role === "manager" ? "var(--cyan-dim)" : "var(--amber-dim)",
                                        color: profile?.role === "manager" ? "var(--cyan)" : "var(--amber)",
                                    }}>
                                        {profile?.role}
                                    </span>
                                </div>
                            </div>

                            {/* Edit name */}
                            <div className="form-field" style={{ marginBottom: "16px" }}>
                                <label>Full Name</label>
                                <input
                                    value={form.full_name}
                                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                    placeholder="Your full name"
                                />
                            </div>

                            <div className="form-field" style={{ marginBottom: "16px" }}>
                                <label>Email Address</label>
                                <input
                                    value={user?.email ?? ""}
                                    disabled
                                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                                />
                                <p style={{ fontSize: "11px", color: "var(--text3)", marginTop: "4px" }}>
                                    Email cannot be changed
                                </p>
                            </div>

                            <button
                                className="save-btn"
                                onClick={handleUpdateProfile}
                                disabled={saving}
                            >
                                {saving ? "Saving..." : "💾 Save Changes"}
                            </button>
                        </div>
                    )}

                    {/* ── Password Tab ── */}
                    {tab === "password" && (
                        <div className="wh-section">
                            <p className="wh-section-title">🔐 Change Password</p>

                            <div className="form-field" style={{ marginBottom: "12px" }}>
                                <label>New Password</label>
                                <input
                                    type="password"
                                    value={pwForm.newPw}
                                    onChange={(e) => setPwForm({ ...pwForm, newPw: e.target.value })}
                                    placeholder="Min. 6 characters"
                                />
                            </div>

                            <div className="form-field" style={{ marginBottom: "16px" }}>
                                <label>Confirm New Password</label>
                                <input
                                    type="password"
                                    value={pwForm.confirm}
                                    onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                                    placeholder="Repeat new password"
                                />
                            </div>

                            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                <button
                                    className="save-btn"
                                    onClick={handleChangePassword}
                                    disabled={saving}
                                >
                                    {saving ? "Changing..." : "🔐 Change Password"}
                                </button>
                                <button
                                    className="cancel-btn"
                                    onClick={handleForgotPassword}
                                >
                                    📧 Send Reset Email
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}